import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { KeyRound, Mail, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react'
import ReCAPTCHA from 'react-google-recaptcha'
import { authService } from '@/services/auth'
import { getErrorMessage } from '@/services/api'
import { useRecaptchaSiteKey } from '@/hooks/useRecaptchaSiteKey'

export default function FirstTimeLogin() {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  // Domain-aware: the main-site key cannot render on a reseller domain.
  const recaptchaSiteKey = useRecaptchaSiteKey()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setError(null)

    if (!email.trim()) {
      setError('Please enter your email address.')
      return
    }
    if (recaptchaSiteKey && !recaptchaToken) {
      setError('Please complete the security check first.')
      return
    }

    setSubmitting(true)
    try {
      const res = await authService.resendMigrationWelcome(email.trim(), recaptchaToken || '')
      setMessage(res.message)
      setEmail('')
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSubmitting(false)
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-primary-50 to-pink-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 px-6 py-8 text-white text-center">
            <div className="inline-flex p-3 bg-white/20 rounded-2xl mb-4">
              <KeyRound className="w-8 h-8" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">First Time Logging In?</h1>
            <p className="text-sm text-white/90">
              Moved over from our old platform? Enter your email and we'll send your set-up link.
            </p>
          </div>

          <div className="p-6 sm:p-8 space-y-5">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
              <p className="font-semibold text-gray-900">How it works</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-600">
                <li>Enter the email address we have on file for your account.</li>
                <li>If your account is being migrated, we'll email a fresh set-up link.</li>
                <li>Click the link, set a new password, confirm your mobile — done.</li>
              </ol>
            </div>

            {message && (
              <div className="flex items-start gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-green-800 text-sm">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>{message}</p>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              {recaptchaSiteKey && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={setRecaptchaToken}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !email.trim() || (!!recaptchaSiteKey && !recaptchaToken)}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-semibold shadow hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : (
                  <>
                    Send my set-up link
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="text-center text-sm text-gray-600 pt-2 border-t border-gray-100">
              Already set up your account?{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                Log in here
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
